var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDataFromSessionStorage } from "../config/configHelper";
import { HttpService } from "../library/http/http.service";
import { getCurrentUser } from "@elixir/auth";
const TRANSFERRED_TO_EUDB = "Transferred To EUDB Instance";
/**
 * Compute a richness score for a commercial escalation entry.
 * Higher score = more populated fields = more likely to be the authoritative record.
 *
 * Weights:
 * - uemTrackingId (+3): primary UEM pipeline key; present on all records that
 *   passed through the escalation submission flow. Weighted highest because it
 *   links the record to the UEM search index and downstream workflows.
 * - flowType (+1): indicates escalation flow classification (e.g. "Customer or
 *   Partner Programs"). Present on EFD-submitted records but absent on stubs.
 * - submitterEmail (+1): set by the UEM submission pipeline. Absent on
 *   "Transferred To EUDB Instance" stub records returned by GetHelp.
 *
 * Penalty:
 * - status === "Transferred To EUDB Instance" (-10): these are stub/placeholder
 *   records created in the ROW database when a case is forwarded to the EU
 *   instance. They carry the same escalationRequestNumber but lack real case
 *   data. The large penalty ensures the corresponding EU record always wins.
 */
export function getFieldRichnessScore(esc) {
    var _a;
    let score = 0;
    if (esc.uemTrackingId)
        score += 3;
    if (typeof esc.flowType === "string" && esc.flowType.trim().length > 0)
        score += 1;
    if (typeof ((_a = esc.submitterInfo) === null || _a === void 0 ? void 0 : _a.submitterEmail) === "string" &&
        esc.submitterInfo.submitterEmail.trim().length > 0)
        score += 1;
    if (esc.status === TRANSFERRED_TO_EUDB)
        score -= 10;
    return score;
}
const hasCompleteStageProgression = (escalation) => {
    var _a, _b, _c, _d;
    return escalation.isEscalationManagerCase === true &&
        Number.isInteger(escalation.stageCount) &&
        ((_a = escalation.stageCount) !== null && _a !== void 0 ? _a : 0) > 0 &&
        Number.isInteger(escalation.currentStageIndex) &&
        ((_b = escalation.currentStageIndex) !== null && _b !== void 0 ? _b : -1) >= 0 &&
        ((_c = escalation.currentStageIndex) !== null && _c !== void 0 ? _c : 0) < ((_d = escalation.stageCount) !== null && _d !== void 0 ? _d : 0) &&
        typeof escalation.stageText === "string" &&
        escalation.stageText.trim().length > 0;
};
const preserveCompleteStageProgression = (preferred, duplicate) => {
    if (hasCompleteStageProgression(preferred) ||
        !hasCompleteStageProgression(duplicate)) {
        return preferred;
    }
    return Object.assign(Object.assign({}, preferred), { isEscalationManagerCase: duplicate.isEscalationManagerCase, stageCount: duplicate.stageCount, currentStageIndex: duplicate.currentStageIndex, stageText: duplicate.stageText });
};
/**
 * Deduplicate commercial escalations by escalationRequestNumber.
 *
 * Collision resolution (two records sharing the same escalationRequestNumber):
 * 1. If exactly one of the colliding records is the EU record (isEU === true),
 *    that record always wins — independent of richness score. EU records are the
 *    authoritative source of truth after a case is transferred to the EU (EUDB)
 *    instance, so the live EU case must never be hidden by its global copy. This
 *    is resilient to bug 39145679, where the backend syncs the closed global copy
 *    to status "Completed" (not "Transferred To EUDB Instance"), so the
 *    status-string penalty in getFieldRichnessScore no longer fires and the
 *    closed global record would otherwise win the tie.
 * 2. Otherwise (neither or both are EU), the entry with the highest richness
 *    score wins.
 * 3. When the selected record lacks a complete EM progression contract, retain
 *    a complete contract from its duplicate. This preserves the authoritative
 *    record's lifecycle and region data without dropping UI-only progression
 *    fields during the merge.
 *
 * Entries without an escalationRequestNumber pass through undeduped.
 *
 * Note: the returned array places keyed entries first, then no-key entries.
 * This reordering is harmless because the downstream consumer (HomePage.tsx
 * ~L298) re-sorts by submittedOn descending before rendering.
 */
export function deduplicateEscalations(escalations) {
    const bestByKey = new Map();
    const noKeyEntries = [];
    escalations.forEach((esc) => {
        const key = esc.escalationRequestNumber;
        // Falsy check intentionally treats "" the same as null/undefined —
        // empty strings are not meaningful dedup keys.
        if (!key) {
            noKeyEntries.push(esc);
            return;
        }
        const escScore = getFieldRichnessScore(esc);
        const existing = bestByKey.get(key);
        if (!existing) {
            bestByKey.set(key, { esc, score: escScore });
            return;
        }
        // EU records are authoritative after an EU (EUDB) transfer. When exactly one
        // of the colliding records is the EU record, always keep it — regardless of
        // richness score (bug 39145679: the closed global copy is synced to
        // "Completed" so it would otherwise win and hide the live EU case).
        // Normalize with === true so a global record whose isEU is undefined is not
        // mistaken for a different value than an explicit false.
        const isEuRecord = esc.isEU === true;
        const existingIsEuRecord = existing.esc.isEU === true;
        if (isEuRecord !== existingIsEuRecord) {
            if (isEuRecord) {
                bestByKey.set(key, {
                    esc: preserveCompleteStageProgression(esc, existing.esc),
                    score: escScore,
                });
            }
            else {
                existing.esc = preserveCompleteStageProgression(existing.esc, esc);
            }
            return;
        }
        // Neither or both are EU: fall back to richness scoring.
        if (escScore > existing.score) {
            bestByKey.set(key, {
                esc: preserveCompleteStageProgression(esc, existing.esc),
                score: escScore,
            });
        }
        else {
            existing.esc = preserveCompleteStageProgression(existing.esc, esc);
        }
    });
    return [
        ...Array.from(bestByKey.values()).map(({ esc }) => esc),
        ...noKeyEntries,
    ];
}
export function getMyEscalations() {
    var _a, _b, _c;
    return __awaiter(this, void 0, void 0, function* () {
        let response;
        let euresponse;
        try {
            const http = new HttpService();
            const details = getCurrentUser();
            if (details !== undefined &&
                details !== null &&
                details.account !== undefined &&
                details.account !== null &&
                details.account.length > 0) {
                const email = `${details === null || details === void 0 ? void 0 : details.account}`;
                response = yield http.get(getDataFromSessionStorage("globalFrontdoorbaseurl"), getDataFromSessionStorage("myescalationsRoute") + email, undefined, undefined);
                // Fetching Data from EU Instance if enabled
                try {
                    if (((_a = getDataFromSessionStorage("isEUEnabled")) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === "true") {
                        euresponse = yield http.get(getDataFromSessionStorage("euFrontdoorbaseurl"), getDataFromSessionStorage("myescalationsRoute") + email, undefined, undefined);
                        const globalEscalations = (_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.result;
                        const euEscalations = (_c = euresponse === null || euresponse === void 0 ? void 0 : euresponse.data) === null || _c === void 0 ? void 0 : _c.result;
                        // Guard: only merge when the global response actually returned data.
                        if (globalEscalations) {
                            if (!globalEscalations.commercialEscalation) {
                                globalEscalations.commercialEscalation = [];
                            }
                            // Appending EU Cases to Global Instance
                            // Setting isEU flag to identify EU Cases.
                            if (euEscalations === null || euEscalations === void 0 ? void 0 : euEscalations.commercialEscalation) {
                                euEscalations.commercialEscalation.forEach((escalation) => {
                                    escalation.isEU = true;
                                    globalEscalations.commercialEscalation.push(escalation);
                                });
                            }
                            // Deduplicate merged entries by escalationRequestNumber,
                            // keeping the richest record per key.
                            globalEscalations.commercialEscalation = deduplicateEscalations(globalEscalations.commercialEscalation);
                            response.data.result = globalEscalations;
                        }
                    }
                }
                catch (e) {
                    const error = { message: e };
                    // Placeholder - has to be pushed to App-insights/Geneva
                    console.log(error);
                }
            }
        }
        catch (e) {
            const error = { message: e };
            throw error;
        }
        return response;
    });
}
